export const supportedLocales = [
  'tr',
  'en',
  'de',
  'es',
  'fr',
  'ru',
  'it',
  'pt',
  'sv',
  'nl',
  'ar',
  'hi',
  'id',
  'ja',
  'ko',
  'pl',
  'th',
] as const

export type SupportedLocale = (typeof supportedLocales)[number]

export const contentTypes = ['simulation', 'mini-game', 'mini-quiz'] as const

export type ContentType = (typeof contentTypes)[number]

export type ContentOrientation = 'portrait' | 'landscape' | 'adaptive'

export type GameControlProfile =
  | 'board'
  | 'driving'
  | 'platform'
  | 'action'
  | 'puzzle'
  | 'sports'
  | 'cards'
  | 'adaptive'

export type ScoreDirection = 'higher' | 'lower'

export const communityCategoryIds = [
  'science',
  'maps',
  'biology',
  'health',
  'mathematics',
  'technology',
  'engineering',
  'finance',
  'history',
  'society',
  'arts',
  'education',
  'daily-life',
  'other',
] as const

export type CommunityCategoryId = (typeof communityCategoryIds)[number]

export type SimulationCategory =
  | CommunityCategoryId
  | 'world'
  | 'entertainment'

export type RendererKind =
  | 'space-orbit'
  | 'bio-process'
  | 'finance-chart'
  | 'decision-model'
  | 'branching-timeline'

export type RendererKindV2 =
  | 'svg-process'
  | 'pixi-particles'
  | 'three-scene'
  | 'matter-physics'
  | 'echarts-data'
  | 'network-flow'
  | 'map'
  | 'timeline'

export type RendererKindV3 = RendererKindV2
  | 'freeform-html'

export type ExperienceKindV3 =
  | 'mechanism'
  | 'particles'
  | 'physics'
  | 'spatial'
  | 'data'
  | 'network'
  | 'map'
  | 'timeline'
  | 'comparison'
  | 'decision'

export type ControlKindV3 =
  | 'range'
  | 'toggle'
  | 'segment'
  | 'scrubber'
  | 'node-select'
  | 'step-select'

export interface ControlOptionV3 {
  value: number
  label: string
  description?: string
}

export interface ControlSpecV3 {
  id: string
  kind: ControlKindV3
  label: string
  description: string
  unit?: string
  min: number
  max: number
  step: number
  defaultValue: number
  format?: 'number' | 'currency' | 'percent' | 'compact'
  options?: ControlOptionV3[]
}

export type ExpressionAstNodeV3 =
  | { id: string; type: 'literal'; value: number }
  | { id: string; type: 'variable'; variableId: string }
  | { id: string; type: 'fact'; factId: string }
  | {
      id: string
      type: 'operation'
      operator:
        | 'add'
        | 'subtract'
        | 'multiply'
        | 'divide'
        | 'power'
        | 'min'
        | 'max'
        | 'normalize'
        | 'clamp'
        | 'lerp'
      operandIds: string[]
    }
  | {
      id: string
      type: 'condition'
      operator: 'gt' | 'gte' | 'lt' | 'lte' | 'eq'
      leftId: string
      rightId: string
      whenTrueId: string
      whenFalseId: string
    }
  | {
      id: string
      type: 'lookup'
      inputId: string
      entries: Array<{ key: number; value: number }>
      fallback: number
    }

// Read-only compatibility types for older structured V3 records. The active
// single-pass generator does not expose an AST to Gemini or evaluate one.
export interface ExpressionNodeV3 {
  rootId: string
  nodes: ExpressionAstNodeV3[]
}

export interface InteractionPlanV3 {
  version: '3.0'
  artifactMode: 'sandbox-html'
  prompt: string
  locale: SupportedLocale
  normalizedIntent: string
  subject: string
  learningGoal: string
  learningStory: string
  experienceKind: ExperienceKindV3
  visualModel: {
    subjectRepresentation: string
    essentialParts: string[]
    causalRelationships: string[]
    forbiddenShortcuts: string[]
  }
  variables: Array<{
    id: string
    label: string
    kind: ControlKindV3
    learningRole: string
    unit?: string
  }>
  observables: Array<{
    id: string
    label: string
    learningRole: string
    unit?: string
  }>
  interactionStory: {
    initialState: string
    userAction: string
    visibleResponse: string
    learningOutcome: string
  }
  rendererComposition: {
    primary: RendererKindV3
    secondary: Array<'annotations' | 'mini-chart' | 'kpis' | 'legend'>
  }
  requiresGrounding: boolean
  requiresMap: boolean
  requiresImages: boolean
  accuracyClass:
    | 'scientific'
    | 'educational-model'
    | 'scenario'
    | 'counterfactual'
  questions: PlanningQuestion[]
  answerOutline: string[]
  safetyNotes: string[]
}

export interface LocalizedText {
  title: string
  description: string
  question: string
  outcomeLabel: string
}

export interface SimulationControl {
  id: string
  label: string
  unit?: string
  min: number
  max: number
  step: number
  defaultValue: number
  format?: 'number' | 'currency' | 'percent' | 'compact'
}

export interface SimulationPreset {
  id: string
  label: string
  values: Record<string, number>
}

export interface SimulationSource {
  title: string
  publisher: string
  url: string
  year: number
}

export interface SimulationStats {
  opens: number
  completions: number
  likes: number
  saves: number
  shares: number
  remixes: number
}

export interface SimulationSpecV1 {
  schemaVersion: '1.0'
  runtimeVersion: '1.0'
  id: string
  slug: string
  category: SimulationCategory
  renderer: RendererKind
  accent: string
  cover: {
    eyebrow: string
    metric: string
    gradient: [string, string, string]
  }
  localeKeys: Record<SupportedLocale, LocalizedText>
  controls: SimulationControl[]
  presets: SimulationPreset[]
  kpis: Array<{ id: string; label: string; unit?: string }>
  sources: SimulationSource[]
  accuracy: {
    grade: 'scientific' | 'educational-model' | 'scenario'
    score: number
    note: string
    reviewedAt: string
  }
  stats: SimulationStats
  author: {
    id: string
    name: string
    handle: string
    avatar: string
    verified: boolean
  }
  version: {
    current: number
    changelog: Array<{ version: number; date: string; note: string }>
  }
  lineage?: {
    parentId?: string
    rootId: string
  }
}

export interface ExperimentSnapshot {
  simulationId: string
  values: Record<string, number>
  artifactState?: Record<string, unknown>
  progress: number
  updatedAt: string
}

export interface OperationReceipt {
  id: string
  operation:
    | 'plan'
    | 'generate'
    | 'repair'
    | 'revise'
    | 'translate'
    | 'moderate'
    | 'export'
  model:
    | 'gemini-2.5-flash'
    | 'gemini-3.1-flash-lite'
    | 'gemini-3.5-flash-lite'
    | 'gemini-3.6-flash'
    | 'gemini-3.7-flash'
    | 'gpt-5.6-luna'
  status: 'completed' | 'failed' | 'pending'
  usage?: {
    inputTokens: number
    cachedInputTokens: number
    cacheWriteTokens?: number
    outputTokens: number
    thinkingTokens: number
    toolTokens: number
    totalTokens: number
  }
  costMicroUsd?: number
  pricingVersion?: string
  durationMs: number
  retryCount: number
  createdAt: string
  reconciled?: boolean
  reconciliationStatus?: string
}

export interface PlanningQuestion {
  id: string
  question: string
  reason: string
  options?: string[]
  required: boolean
}

export interface SimulationPlanV2 {
  version: '2.0'
  prompt: string
  locale: SupportedLocale
  normalizedIntent: string
  subject: string
  learningGoal: string
  renderer: RendererKindV2
  requiresGrounding: boolean
  requiresMap: boolean
  requiresImages: boolean
  accuracyClass:
    | 'scientific'
    | 'educational-model'
    | 'scenario'
    | 'counterfactual'
  questions: PlanningQuestion[]
  answerOutline: string[]
  safetyNotes: string[]
}

export type FormulaNode =
  | { type: 'constant'; value: number }
  | { type: 'control'; id: string }
  | { type: 'fact'; id: string }
  | {
      type: 'operation'
      operator: 'add' | 'subtract' | 'multiply' | 'divide' | 'power' | 'min' | 'max'
      operands: FormulaNode[]
    }

export interface FactLedgerV1 {
  version: '1.0'
  facts: Array<{
    id: string
    label: string
    value: string | number | boolean
    unit?: string
    kind: 'definition' | 'constant' | 'input' | 'calculated' | 'claim' | 'assumption'
    sourceIds: string[]
    confidence: number
  }>
  sources: Array<{
    id: string
    title: string
    publisher: string
    url: string
    accessedAt: string
    validFrom?: string
    validTo?: string
  }>
  assumptions: string[]
  uncertainty: string
  validity: { from?: string; to?: string }
}

export interface AnswerBlockV1 {
  id: string
  type:
    | 'paragraph'
    | 'bullets'
    | 'numbered'
    | 'table'
    | 'formula'
    | 'steps'
    | 'callout'
    | 'timeline'
    | 'sources'
    | 'result'
    | 'code'
  title?: string
  text?: string
  items?: string[]
  headers?: string[]
  rows?: string[][]
  formula?: string
  language?: string
  factIds: string[]
  sourceIds: string[]
}

export interface AnswerSpecV1 {
  version: '1.0'
  locale: SupportedLocale
  title: string
  shortAnswer: string
  blocks: AnswerBlockV1[]
}

export interface MapSceneSpecV1 {
  version: '1.0'
  id: string
  center: [number, number]
  zoom: number
  pitch: number
  bearing: number
  validFrom?: string
  validTo?: string
  hideModernLabels: boolean
  layers: Array<{
    id: string
    type: 'markers' | 'line' | 'polygon' | 'heatmap' | 'grid' | 'hexagon' | 'choropleth' | 'arc' | 'trips' | 'raster'
    label: string
    visible: boolean
    interactive: boolean
    data: Array<Record<string, unknown>>
    sourceId: string
    license: string
    attribution: string
    confidence: number
    approximate: boolean
  }>
}

export interface SimulationSpecV2 {
  schemaVersion: '2.0'
  runtimeVersion: '2.0'
  id: string
  slug: string
  category: SimulationCategory
  renderer: RendererKindV2
  locale: SupportedLocale
  title: string
  question: string
  description: string
  accent: string
  palette: string[]
  controls: SimulationControl[]
  presets: SimulationPreset[]
  kpis: Array<{
    id: string
    label: string
    unit?: string
    format?: SimulationControl['format']
    factId: string
    formula: FormulaNode
  }>
  scene: {
    kind: string
    nodes: Array<{
      id: string
      label: string
      type: string
      x: number
      y: number
      size: number
      color: string
      factId?: string
    }>
    edges: Array<{ id: string; from: string; to: string; label?: string }>
    timeline: Array<{ at: number; label: string; description: string; factIds: string[] }>
    bindings: Array<{
      controlId: string
      targetType: 'node' | 'edge' | 'timeline' | 'camera' | 'layer' | 'series' | 'global'
      targetId: string
      property: 'x' | 'y' | 'size' | 'opacity' | 'speed' | 'rotation' | 'value' | 'radius' | 'intensity' | 'time' | 'visibility'
      scale: number
      offset: number
    }>
    chart?: { xLabel: string; yLabel: string; series: Array<{ id: string; label: string; factId?: string }> }
    mapSceneId?: string
  }
  accuracy: {
    grade: 'scientific' | 'educational-model' | 'scenario' | 'counterfactual'
    score: number
    note: string
  }
  sourceIds: string[]
}

export interface GenerationBundleV2 {
  schemaVersion: '2.0'
  factLedger: FactLedgerV1
  simulation: SimulationSpecV2
  answer: AnswerSpecV1
  mapScene: MapSceneSpecV1 | null
  assetRequests: Array<{
    id: string
    purpose: 'atmosphere' | 'texture' | 'object' | 'explanatory'
    prompt: string
    orientation: 'portrait' | 'landscape'
    required: boolean
    url?: string
  }>
}

interface SceneEntityV3 {
  id: string
  label: string
  description: string
  color: string
  factId?: string
}

export interface ProcessSceneSpecV3 {
  kind: 'process'
  entities: Array<SceneEntityV3 & {
    shape: 'circle' | 'capsule' | 'rect' | 'membrane' | 'vessel' | 'organ' | 'component'
    x: number
    y: number
    width: number
    height: number
  }>
  flows: Array<{
    id: string
    from: string
    to: string
    label: string
    color: string
    direction: 'forward' | 'reverse' | 'both'
  }>
  zones: Array<{
    id: string
    label: string
    x: number
    y: number
    width: number
    height: number
    color: string
  }>
}

export interface ParticleSceneSpecV3 {
  kind: 'particles'
  emitters: Array<SceneEntityV3 & {
    x: number
    y: number
    rate: number
    radius: number
    particleSize: number
    velocity: number
  }>
  fields: Array<SceneEntityV3 & {
    x: number
    y: number
    radius: number
    strength: number
    polarity: number
  }>
  boundaries: Array<{
    id: string
    label: string
    x1: number
    y1: number
    x2: number
    y2: number
    permeability: number
  }>
}

export interface PhysicsSceneSpecV3 {
  kind: 'physics'
  gravity: { x: number; y: number }
  bodies: Array<SceneEntityV3 & {
    shape: 'circle' | 'rectangle' | 'polygon'
    x: number
    y: number
    width: number
    height: number
    radius: number
    mass: number
    restitution: number
    friction: number
    isStatic: boolean
  }>
  constraints: Array<{
    id: string
    from: string
    to: string
    label: string
    stiffness: number
    length: number
  }>
}

export interface SpatialSceneSpecV3 {
  kind: 'spatial'
  camera: { x: number; y: number; z: number; fov: number }
  objects: Array<SceneEntityV3 & {
    geometry: 'sphere' | 'ring' | 'box' | 'cone' | 'cylinder' | 'field' | 'orbit'
    x: number
    y: number
    z: number
    size: number
    opacity: number
    rotationSpeed: number
  }>
  relationships: Array<{
    id: string
    from: string
    to: string
    label: string
    color: string
  }>
}

export interface DataSceneSpecV3 {
  kind: 'data'
  chartType: 'line' | 'area' | 'bar' | 'scatter' | 'radar' | 'pie'
  xLabel: string
  yLabel: string
  series: Array<{
    id: string
    label: string
    color: string
    points: Array<{ x: number; y: number }>
  }>
  referenceLines: Array<{ id: string; label: string; value: number }>
}

export interface NetworkSceneSpecV3 {
  kind: 'network'
  nodes: Array<SceneEntityV3 & {
    group: string
    importance: number
  }>
  edges: Array<{
    id: string
    from: string
    to: string
    label: string
    relation: 'causes' | 'supports' | 'inhibits' | 'transforms' | 'equivalent' | 'depends-on'
    strength: number
    color: string
  }>
}

export interface MapRendererSpecV3 {
  kind: 'map'
  mapSceneId: string
}

export interface TimelineSceneSpecV3 {
  kind: 'timeline'
  events: Array<{
    id: string
    label: string
    description: string
    at: number
    lane: string
    color: string
    factIds: string[]
  }>
  branches: Array<{
    id: string
    fromEventId: string
    toEventId: string
    label: string
    probability?: number
  }>
}

export type RendererPayloadV3 =
  | ProcessSceneSpecV3
  | ParticleSceneSpecV3
  | PhysicsSceneSpecV3
  | SpatialSceneSpecV3
  | DataSceneSpecV3
  | NetworkSceneSpecV3
  | MapRendererSpecV3
  | TimelineSceneSpecV3

export interface SceneBindingV3 {
  variableId: string
  targetId: string
  mode: 'replace' | 'add' | 'multiply'
  property:
    | 'x'
    | 'y'
    | 'z'
    | 'width'
    | 'height'
    | 'size'
    | 'opacity'
    | 'speed'
    | 'rotation'
    | 'radius'
    | 'intensity'
    | 'visibility'
    | 'strength'
    | 'rate'
    | 'value'
    | 'time'
  expression: ExpressionNodeV3
}

export interface SimulationSpecV3 {
  schemaVersion: '3.0'
  runtimeVersion: '3.0'
  id: string
  slug: string
  contentType?: ContentType
  contentCategory?: string
  orientation?: ContentOrientation
  controlProfile?: GameControlProfile
  scoreMetric?: string
  scoreDirection?: ScoreDirection
  questionCount?: number
  category: SimulationCategory
  renderer: RendererKindV3
  experienceKind: ExperienceKindV3
  locale: SupportedLocale
  title: string
  question: string
  sceneGuide: {
    whatYouSee: string
    whatToChange: string
    whatToObserve: string
    liveExplanation: string
  }
  accent: string
  palette: string[]
  controls: ControlSpecV3[]
  presets: SimulationPreset[]
  observables: Array<{
    id: string
    label: string
    unit?: string
    format?: SimulationControl['format']
    factId: string
    expression: ExpressionNodeV3
  }>
  accuracy: {
    grade: 'scientific' | 'educational-model' | 'scenario' | 'counterfactual'
    score: number
    note: string
  }
  sourceIds: string[]
  artifact: InteractiveArtifactDescriptorV3
}

export interface InteractiveArtifactDescriptorV3 {
  version: '1.0'
  validatorVersion: '1.0'
  title: string
  contentHash: string
  capabilities: {
    canvas2d: boolean
    svg: boolean
    webgl: boolean
    webAudio: boolean
  }
}

export interface InteractiveArtifactV3 extends InteractiveArtifactDescriptorV3 {
  html: string
}

export interface QuizQuestionV1 {
  id: string
  prompt: string
  choices: [string, string, string, string]
  correctIndex: number
  explanation: string
}

export interface QuizSpecV1 {
  version: '1.0'
  title: string
  description: string
  category: string
  locale: SupportedLocale
  durationSeconds: number
  questions: QuizQuestionV1[]
}

export interface GenerationBundleV3 {
  schemaVersion: '3.0'
  interactionPlan: InteractionPlanV3
  factLedger: FactLedgerV1
  simulation: SimulationSpecV3
  artifact: InteractiveArtifactV3
  quiz?: QuizSpecV1
  /**
   * Kept optional only so previously generated V3 records can still be read.
   * New productions do not generate a second, article-style AI answer.
   */
  answer?: AnswerSpecV1
  mapScene: MapSceneSpecV1 | null
  assetRequests: GenerationBundleV2['assetRequests']
}

export type GenerationBundle = GenerationBundleV2 | GenerationBundleV3
export type GeneratedSimulationSpec = SimulationSpecV2 | SimulationSpecV3

export interface CreatorSummary {
  id: string
  name: string
  handle: string
  avatar: string
  username?: string
}

export interface SimulationSummary {
  id: string
  ownerId: string
  title: string
  question: string
  description: string
  slug: string
  contentType?: ContentType
  contentCategory?: string
  orientation?: ContentOrientation
  controlProfile?: GameControlProfile
  scoreMetric?: string
  scoreDirection?: ScoreDirection
  questionCount?: number
  category: SimulationCategory
  renderer: RendererKindV3
  discoveryShelf?: string
  hasLivePreview?: boolean
  previewVersion?: '1.0'
  accent: string
  palette: string[]
  creator: CreatorSummary
  status: 'draft' | 'published'
  visibility: 'private' | 'public'
  viewCount: number
  stats: SimulationStats
  publishedAt?: unknown
  createdAt?: unknown
  updatedAt?: unknown
}

export function normalizeContentType(value: unknown): ContentType {
  return contentTypes.includes(value as ContentType)
    ? value as ContentType
    : 'simulation'
}
